import { useEffect, useState, useCallback, useRef } from "react";
import { RefreshCw } from "lucide-react";
import { APP_VERSION } from "../version";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// F12: Zeigt einen nicht-blockierenden Hinweis, wenn das geladene Browser-Bundle veraltet ist.
// Zwei Signale: (1) die vom Server gemeldete Version ändert sich WÄHREND der Sitzung
// (es wurde zwischenzeitlich neu ausgerollt), (2) die gemeldete Version weicht von der
// gebauten APP_VERSION ab. Kein Auto-Reload; bei Netzfehler kein Hinweis.
export const VersionBanner = () => {
  const [veraltet, setVeraltet] = useState(false);
  const ersteVersion = useRef(null);

  const pruefen = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/version`, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      const v = data?.version;
      if (!v) return;
      if (ersteVersion.current === null) {
        ersteVersion.current = v;              // erster Abruf: nur merken
      } else if (v !== ersteVersion.current) {
        setVeraltet(true);                     // Signal 1: während der Sitzung neu ausgerollt
      }
      if (APP_VERSION && v !== APP_VERSION) {
        setVeraltet(true);                     // Signal 2: veraltetes Bundle geladen
      }
    } catch {
      // Netzfehler: bewusst nichts anzeigen
    }
  }, []);

  useEffect(() => {
    pruefen();
    const onFocus = () => pruefen();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(pruefen, 10 * 60 * 1000); // alle 10 Minuten
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, [pruefen]);

  const [laedt, setLaedt] = useState(false);

  const neuLaden = useCallback(async () => {
    if (laedt) return;                 // Doppelklicks abfangen
    setLaedt(true);
    try {
      // Notbremse: Haengt die Verbindung, soll der Knopf nicht ewig blockieren -
      // nach 5 Sekunden greift der Ersatzweg unten.
      const abbruch = new AbortController();
      const wecker = setTimeout(() => abbruch.abort(), 5000);

      // Erzwingt eine frische Uebertragung vom Server UND aktualisiert dabei den
      // HTTP-Cache-Eintrag. Der anschliessende Reload liest dann die neue Fassung.
      // Ohne diesen Schritt liefert window.location.reload() dieselbe veraltete
      // index.html zurueck, wegen der der Balken ueberhaupt erst erscheint.
      const antwort = await fetch(window.location.href, {
        cache: "reload",
        signal: abbruch.signal,
      });
      // Den Rumpf VOLLSTAENDIG lesen. await fetch() liefert bereits zurueck,
      // sobald die Kopfzeilen da sind - der Cache-Eintrag ist zu diesem Zeitpunkt
      // noch nicht fertig geschrieben. Ohne diese Zeile kann der Reload darunter
      // gegen den alten Eintrag laufen und der Fix greift nur manchmal.
      await antwort.text();
      clearTimeout(wecker);

      window.location.reload();
    } catch {
      // Netzfehler, Zeitueberschreitung oder blockierter fetch: Sicherheitsnetz
      // ueber eine eindeutige Adresse. Ein abweichender URL kann per Definition
      // nicht aus dem Cache-Eintrag der alten Adresse bedient werden.
      // Bestehende Query-Parameter bleiben erhalten, damit Links mit Token
      // (z.B. die Lehrer-Bestaetigung) nicht kaputtgehen.
      const url = new URL(window.location.href);
      url.searchParams.set("_v", Date.now().toString());
      window.location.replace(url.toString());
    }
  }, [laedt]);

  if (!veraltet) return null;

  return (
    <div
      data-testid="version-update-banner"
      className="fixed top-0 inset-x-0 z-[100] bg-amber-500 text-amber-950 shadow-md"
    >
      <div className="max-w-5xl mx-auto flex items-center justify-center gap-3 px-4 py-2 text-sm font-medium">
        <span>Sie arbeiten mit einer veralteten Version. Neue Funktionen fehlen, bis Sie neu laden.</span>
        <button
          type="button"
          onClick={neuLaden}
          disabled={laedt}
          data-testid="version-reload-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-amber-950 text-amber-50 px-3 py-1 text-xs font-semibold hover:bg-amber-900 transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${laedt ? "animate-spin" : ""}`} />
          {laedt ? "Wird geladen…" : "Jetzt neu laden"}
        </button>
      </div>
    </div>
  );
};
